// Lion Auto's Rows filter (criteria.rowsInclude): the row labels a pricer
// ticks in a group's Advanced filters. Only the ticked rows match, for our
// listings and the market's; nothing ticked = every row. Here: a row label
// as the server compares it, the rows' natural order, the picked list as the
// server takes it, the rows on offer in the group's sections (ours and the
// market's, with counts) and the words the editor shows for a pick. Pure;
// tested in lionAutoRows.test.js.
import { matchesSelectedSection } from './lionAutoListings';

// The server's caps: at most 100 picked rows, each 1..20 characters
export const MAX_ROWS_INCLUDE = 100;
export const MAX_ROW_LABEL = 20;

/** A row label as written: inner whitespace collapsed, trimmed ("" for none) */
export function rowText(raw) {
  if (raw === null || raw === undefined) return '';
  return String(raw).replace(/\s+/g, ' ').trim();
}

/**
 * What the server compares rows by (compMatchRules parseRow's `norm`):
 * upper-cased, without a leading "ROW " — "row a", "A" and "Row  A" are one
 * row
 */
export function rowKey(raw) {
  return rowText(raw).toUpperCase().replace(/^ROW /, '');
}

const NUMERIC = /^\d+$/;
const LETTERS = /^[A-Z]+$/;
// 0 = numeric rows, 1 = letter rows, 2 = anything else (GA, PIT, A1, VIP …)
const rowClass = (key) => (NUMERIC.test(key) ? 0 : LETTERS.test(key) ? 1 : 2);
const cmp = (a, b) => (a < b ? -1 : a > b ? 1 : 0);

/**
 * Natural row order: numeric rows by number (1, 2, 10), then letter rows by
 * length, then alphabetically (A … Z, AA, AB … ZZ, AAA), then any other
 * label alphabetically, numbers inside it read as numbers (GA2 before GA10).
 * Labels that are the same row (same key, or "01" and "1") keep a stable
 * order by their text.
 */
export function compareRows(a, b) {
  const ka = rowKey(a);
  const kb = rowKey(b);
  const ca = rowClass(ka);
  const cb = rowClass(kb);
  if (ca !== cb) return ca - cb;
  let d = 0;
  if (ca === 0) {
    // Exact for any length: no leading zeros, then shorter = smaller
    const na = ka.replace(/^0+(?=\d)/, '');
    const nb = kb.replace(/^0+(?=\d)/, '');
    d = na.length - nb.length || cmp(na, nb);
  } else if (ca === 1) {
    d = ka.length - kb.length || cmp(ka, kb);
  } else {
    d = ka.localeCompare(kb, 'en', { numeric: true }) || cmp(ka, kb);
  }
  return d || cmp(rowText(a), rowText(b));
}

/**
 * The picked rows as the server takes them: strings only, whitespace
 * collapsed and trimmed, blanks and labels over 20 characters dropped (a cut
 * label would be another row), one entry per row (case and a leading "ROW "
 * ignored; the first spelling stays), at most 100, in natural order (the
 * order means nothing, so the same set always reads the same: one preview,
 * one dirty check).
 */
export function cleanRowsInclude(rows) {
  const out = [];
  const seen = new Set();
  for (const r of Array.isArray(rows) ? rows : []) {
    if (typeof r !== 'string') continue;
    const label = rowText(r);
    const key = rowKey(label);
    if (!label || label.length > MAX_ROW_LABEL || !key || seen.has(key)) continue;
    seen.add(key);
    out.push(label);
    if (out.length >= MAX_ROWS_INCLUDE) break;
  }
  return out.sort(compareRows);
}

/**
 * Ticks (on) or unticks (off) rows in a pick: `labels` join it (cleaned,
 * capped at 100: the rows already picked stay first) or every spelling of
 * them leaves it.
 */
export function withRows(picked, labels, on) {
  const current = Array.isArray(picked) ? picked : [];
  const list = Array.isArray(labels) ? labels : [];
  if (on) return cleanRowsInclude([...current, ...list]);
  const drop = new Set(list.map(rowKey));
  return current.filter(p => !drop.has(rowKey(p)));
}

const NO_ROWS = Object.freeze({ rows: Object.freeze([]), blankOwn: 0, blankComps: 0 });

/**
 * The rows on offer in a group's sections: every row label of our listings
 * and the market's there (Compare's section rule), one entry per row with
 * how many of each sit in it, in natural order; plus how many listings have
 * no row label (they only match while nothing is ticked). No sections, or no
 * pricing yet: nothing on offer.
 * @param {object} pricing  useEventPricing data (myInventory, competitorListings)
 * @param {{id, name}[]} sections  the group's sections as the map keys them
 * @returns {{ rows: {key, label, own, comps}[], blankOwn: number, blankComps: number }}
 */
export function rowOptionsFrom(pricing, sections) {
  if (!pricing || !Array.isArray(sections) || sections.length === 0) return NO_ROWS;
  const byKey = new Map();
  let blankOwn = 0;
  let blankComps = 0;
  const add = (l, own) => {
    if (!l || typeof l !== 'object' || !matchesSelectedSection(l, sections)) return;
    const label = rowText(l.row);
    if (!label) {
      if (own) blankOwn += 1;
      else blankComps += 1;
      return;
    }
    const key = rowKey(label);
    let entry = byKey.get(key);
    if (!entry) {
      entry = { key, label, own: 0, comps: 0 };
      byKey.set(key, entry);
    }
    if (own) entry.own += 1;
    else entry.comps += 1;
  };
  for (const l of Array.isArray(pricing.myInventory) ? pricing.myInventory : []) add(l, true);
  for (const l of Array.isArray(pricing.competitorListings) ? pricing.competitorListings : []) add(l, false);
  const rows = [...byKey.values()].sort((a, b) => compareRows(a.label, b.label));
  return { rows, blankOwn, blankComps };
}

/**
 * The dropdown's list: the rows on offer, each marked picked or not (and too
 * long to pick), plus any picked row no listing sits in now ("missing"), so
 * it can still be unticked — all in natural order.
 * @returns {{key, label, own, comps, picked, missing, tooLong}[]}
 */
export function rowChoices(options, picked) {
  const pickedList = Array.isArray(picked) ? picked : [];
  const pickedKeys = new Set(pickedList.map(rowKey));
  const list = (options?.rows || []).map(o => ({
    ...o,
    picked: pickedKeys.has(o.key),
    missing: false,
    tooLong: o.label.length > MAX_ROW_LABEL,
  }));
  const have = new Set(list.map(o => o.key));
  for (const p of pickedList) {
    const key = rowKey(p);
    if (!key || have.has(key)) continue;
    have.add(key);
    list.push({ key, label: rowText(p), own: 0, comps: 0, picked: true, missing: true, tooLong: false });
  }
  return list.sort((a, b) => compareRows(a.label, b.label));
}

// "A, B, C" while a pick reads at a glance (a few short labels), else null
function shortList(rows, maxRows = 4, maxChars = 24) {
  if (rows.length === 0 || rows.length > maxRows) return null;
  const text = rows.join(', ');
  return text.length <= maxChars ? text : null;
}

/** The dropdown button: "All rows", "Row A", "Rows A, B, C", or "12 rows" */
export function rowsButtonText(rows) {
  const list = Array.isArray(rows) ? rows : [];
  if (list.length === 0) return 'All rows';
  const text = shortList(list);
  if (text) return `${list.length === 1 ? 'Row' : 'Rows'} ${text}`;
  return `${list.length} rows`;
}

/** The collapsed Advanced filters line: null (every row), "Rows A, B" or "Rows: 12 picked" */
export function rowsSummaryText(rows) {
  const list = Array.isArray(rows) ? rows : [];
  if (list.length === 0) return null;
  const text = shortList(list);
  if (text) return `${list.length === 1 ? 'Row' : 'Rows'} ${text}`;
  return `Rows: ${list.length} picked`;
}
